// src/components/ProfilePage.tsx

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card.tsx';
import { Button } from './ui/button.tsx';
import { Badge } from './ui/badge.tsx';
import { Input } from './ui/input.tsx';
import { Label } from './ui/label.tsx';
import { 
  Settings, MapPin, Briefcase, Mail, Phone, 
  GraduationCap, Linkedin, Instagram, MessageSquare, 
  Camera, Megaphone, Trash2, Edit2
} from 'lucide-react';
import { ImageWithFallback } from './resim/ImageWithFallback.tsx';
import DEFAULT_PROFILE_IMAGE from '../assets/logo.png';
import DEFAULT_COVER_IMAGE from '../assets/homepage.png';

interface UserProfile {
  id?: string;
  firstName: string;
  lastName: string;
  email: string;
  userType: 'student' | 'alumni' | 'teacher';
  phone?: string;          
  graduationYear?: string; 
  department?: string;     
  company?: string;        
  occupation?: string;     
  city?: string;           
  bio?: string;            
  profileImage?: string;   
  coverImage?: string;     
  memberSince?: string;    
  linkedin?: string;
  instagram?: string;
}

interface AdminAnnouncement { 
  id: number; 
  Title: string; 
  Content: string; 
  Description?: string; 
  Category: string; 
  PublishDate: string; 
  Image?: string | null;
}

export function ProfilePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  const targetUserId = searchParams.get('id'); 
  const currentLoggedInUserId = localStorage.getItem('userId');
  
  // Yetki ve Profil Sahipliği Kontrolleri
  const isOwnProfile = !targetUserId || targetUserId === currentLoggedInUserId;
  const displayUserId = isOwnProfile ? currentLoggedInUserId : targetUserId;
  const isAdmin = localStorage.getItem('isStaff') === 'true'; 

  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isFirstTime, setIsFirstTime] = useState(false);

  const [formData, setFormData] = useState({
    phoneNumber: '',
    city: '',
    graduationYear: '',
    company: '',
    occupation: '',
    linkedinLink: '',
    instagramLink: '',
    biography: ''
  });

  // --- DUYURU STATE'LERİ ---
  const [announcements, setAnnouncements] = useState<AdminAnnouncement[]>([]);
  const [isPosting, setIsPosting] = useState(false);
  const [editingAnnouncementId, setEditingAnnouncementId] = useState<number | null>(null); // Düzenlenen duyurunun ID'si
  const [newAnnouncement, setNewAnnouncement] = useState({ 
      title: '', description: '', content: '', category: 'GENEL'
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const profileImageInputRef = useRef<HTMLInputElement>(null);
  const coverImageInputRef = useRef<HTMLInputElement>(null);



  const getFullImageUrl = (urlPath: string | undefined | null) => {
    if (!urlPath) return '';
    if (urlPath.startsWith('http://') || urlPath.startsWith('https://')) return urlPath;
    const baseUrl = api.defaults.baseURL?.replace('/api', '') || 'https://sdal-project-backend.onrender.com';
    return `${baseUrl}${urlPath}`;
  };

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        if (!displayUserId) throw new Error('Kullanıcı ID bulunamadı');

        setLoading(true);
        
        const [profileRes, annRes] = await Promise.all([
          api.get(`/users/${displayUserId}/`),
          api.get(`/announcements/?user_id=${displayUserId}`)
        ]);

        const data = profileRes.data;
        const profileDetails = data.profile || {};

        const profileData: UserProfile = {
          id: data.id,
          firstName: data.FirstName,
          lastName: data.LastName,
          email: data.email,
          userType: data.user_type, 
          phone: profileDetails?.PhoneNumber,
          graduationYear: profileDetails?.GraduationYear,
          department: data.Department, 
          company: profileDetails?.Company,
          occupation: profileDetails?.Occupation,
          city: profileDetails?.City,
          bio: profileDetails?.Biography,
          linkedin: profileDetails?.LinkedinLink,
          instagram: profileDetails?.InstagramLink,
          profileImage: getFullImageUrl(profileDetails?.ProfilePicture), 
          coverImage: getFullImageUrl(profileDetails?.BackgroundPicture),
          memberSince: data.CreatedAt ? new Date(data.CreatedAt).getFullYear().toString() : new Date().getFullYear().toString(),
        };

        setUserProfile(profileData);
        setAnnouncements(annRes.data.results || annRes.data || []);

        setFormData({
          phoneNumber: profileDetails?.PhoneNumber || '',
          city: profileDetails?.City || '',
          graduationYear: profileDetails?.GraduationYear || '',
          company: profileDetails?.Company || '',
          occupation: profileDetails?.Occupation || '',
          linkedinLink: profileDetails?.LinkedinLink || '',
          instagramLink: profileDetails?.InstagramLink || '',
          biography: profileDetails?.Biography || ''
        });

        if (isOwnProfile && location.state?.registeredJustNow === true) {
          setIsFirstTime(true);
          window.history.replaceState({}, document.title);
        } else {
          setIsFirstTime(false);
        }

      } catch (error) {
        console.error("Kullanıcı profili yüklenemedi:", error);
        toast.error("Profil bilgileri yüklenirken bir hata oluştu.");
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [displayUserId, isOwnProfile, location.state]);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>, target: 'profile' | 'cover') => {
    const file = event.target.files?.[0];
    if (!file || !displayUserId) return;

    const imgFormData = new FormData();
    if (target === 'profile') imgFormData.append('profile.ProfilePicture', file);
    if (target === 'cover') imgFormData.append('profile.BackgroundPicture', file);

    const toastId = toast.loading(`${target === 'profile' ? 'Profil' : 'Kapak'} fotoğrafı güncelleniyor...`);

    try {
      const response = await api.patch(`/users/${displayUserId}/`, imgFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const updatedProfile = response.data.profile || {};
      
      setUserProfile(prev => prev ? {
        ...prev,
        profileImage: target === 'profile' ? getFullImageUrl(updatedProfile?.ProfilePicture) : prev.profileImage,
        coverImage: target === 'cover' ? getFullImageUrl(updatedProfile?.BackgroundPicture) : prev.coverImage,
      } : null);

      toast.success('Fotoğraf başarıyla güncellendi!', { id: toastId });
    } catch (error) {
      console.error(error);
      toast.error('Fotoğraf güncellenirken bir hata oluştu.', { id: toastId });
    } finally {
      event.target.value = '';
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
    }
  };

  // --- DUYURU PAYLAŞMA VE GÜNCELLEME ---
  const handleSaveAnnouncement = async () => {
    if (!newAnnouncement.title.trim() || !newAnnouncement.content.trim()) {
      toast.error('Lütfen başlık ve içerik alanlarını doldurun.');
      return;
    }
    
    setIsPosting(true);
    try {
      const formData = new FormData();
      formData.append('Title', newAnnouncement.title);
      formData.append('Content', newAnnouncement.content);
      formData.append('Description', newAnnouncement.description || ''); 
      formData.append('Category', newAnnouncement.category);
      
      if (selectedFile) formData.append('Image', selectedFile);

      const token = localStorage.getItem("access");
      const config = { 
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': token ? `Bearer ${token}` : ''
        } 
      };

      if (editingAnnouncementId) {
        // DÜZENLEME MODU (PATCH)
        const response = await api.patch(`/announcements/${editingAnnouncementId}/`, formData, config);
        setAnnouncements(prev => prev.map(ann => ann.id === editingAnnouncementId ? response.data : ann));
        toast.success('Duyuru başarıyla güncellendi!');
        setEditingAnnouncementId(null);
      } else {
        // YENİ KAYIT MODU (POST)
        const publishDate = new Date().toISOString();
        formData.append('PublishDate', publishDate);
        
        const response = await api.post('/announcements/', formData, config);
        setAnnouncements(prev => [response.data, ...prev]);
        toast.success('Duyuru başarıyla paylaşıldı!');
      }
      
      // Formu Resetle
      setNewAnnouncement({ title: '', description: '', content: '', category: 'GENEL' });
      setSelectedFile(null);
    } catch (error) {
      console.error(error);
      toast.error('İşlem gerçekleştirilirken bir hata oluştu.');
    } finally {
      setIsPosting(false);
    }
  };

  // --- DUYURUYU DÜZENLEME MODUNA ALMA ---
  const handleEditAnnouncementClick = (ann: AdminAnnouncement) => {
    setEditingAnnouncementId(ann.id);
    setNewAnnouncement({
      title: ann.Title,
      description: ann.Description || '',
      content: ann.Content,
      category: ann.Category
    });
    setSelectedFile(null);
    // Sayfayı yazı alanına doğru yumuşakça kaydırır
    window.scrollTo({ top: 400, behavior: 'smooth' });
  };

  // --- DUYURU SİLME ---
  const handleDeleteAnnouncement = async (announcementId: number) => {
    if (!window.confirm("Bu duyuruyu tamamen silmek istediğinize emin misiniz?")) return;

    try {
      await api.delete(`/announcements/${announcementId}/`);
      toast.success("Duyuru başarıyla silindi.");
      setAnnouncements(prev => prev.filter(ann => ann.id !== announcementId));
      if (editingAnnouncementId === announcementId) {
        setEditingAnnouncementId(null);
        setNewAnnouncement({ title: '', description: '', content: '', category: 'GENEL' });
      }
    } catch (error) {
      console.error("Duyuru silinirken hata oluştu:", error);
      toast.error("Duyuru silinemedi. Yetkiniz olmayabilir.");
    }
  };

  const handleFormInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const handleCompleteProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!displayUserId) return;
      const gradYearParsed = formData.graduationYear ? parseInt(formData.graduationYear, 10) : null;
      const payload = {
        PhoneNumber: formData.phoneNumber || null,
        City: formData.city || null,
        GraduationYear: gradYearParsed,
        Company: formData.company || null,
        Occupation: formData.occupation || null,
        LinkedinLink: formData.linkedinLink || null,
        InstagramLink: formData.instagramLink || null, 
        Biography: formData.biography || null
      };

      const response = await api.patch(`/users/${displayUserId}/`, payload);
      const updatedProfile = response.data.profile || {};

      setUserProfile(prev => prev ? {
        ...prev,
        phone: updatedProfile?.PhoneNumber,
        city: updatedProfile?.City,
        graduationYear: updatedProfile?.GraduationYear,
        company: updatedProfile?.Company,
        occupation: updatedProfile?.Occupation,
        linkedin: updatedProfile?.LinkedinLink,
        instagram: updatedProfile?.InstagramLink,
        bio: updatedProfile?.Biography,
      } : null);

      toast.success('Profil bilgileriniz başarıyla kaydedildi!');
      setIsFirstTime(false); 
    } catch (error) {
      console.error(error);
      toast.error('Profil kaydedilirken bir hata oldu.');
    }
  };

  if (loading) return <div className="text-center py-20 text-sm font-medium text-slate-500">Profil Yükleniyor...</div>;
  if (!userProfile) return <div className="text-center py-20 text-sm font-medium text-[#A8322D]">Profil verisi bulunamadı.</div>;

  return (
    <div className="space-y-6 max-w-6xl mx-auto px-4">
      {isFirstTime ? (
        /* YENİ KAYIT EKRANI */
        <div className="space-y-6 max-w-2xl mx-auto pt-4 transition-all duration-300">
          <div className="bg-[#A8322D] text-white p-6 rounded-2xl text-center space-y-2 border border-[#A8322D]/30 shadow-sm">
            <h1 className="text-2xl font-bold">Aramıza Hoş Geldin, {userProfile.firstName}! </h1>
            <p className="text-slate-100/90 text-sm max-w-md mx-auto">
               Hesabın başarıyla oluşturuldu. Toplulukta diğer üyelerle bağ kurabilmen ve rehberde listelenebilmen için lütfen profilini tamamla.
            </p>
          </div>

          <Card className="border-slate-200 shadow-sm rounded-2xl">
            <CardContent className="pt-6">
              <form onSubmit={handleCompleteProfileSubmit} className="space-y-4">
                <h2 className="text-lg font-bold text-slate-900 border-b border-slate-100 pb-2 mb-4">Profil Bilgilerini Tamamla</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="city" className="text-xs font-semibold text-slate-700">Yaşadığınız Şehir</Label>
                    <Input id="city" placeholder="Örn: Ankara" value={formData.city} onChange={handleFormInputChange} className="border-slate-200 focus-visible:ring-[#A8322D]/20 focus-visible:border-[#A8322D]" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="phoneNumber" className="text-xs font-semibold text-slate-700">Telefon Numaranız</Label>
                    <Input id="phoneNumber" placeholder="+90 5..." value={formData.phoneNumber} onChange={handleFormInputChange} className="border-slate-200 focus-visible:ring-[#A8322D]/20 focus-visible:border-[#A8322D]" />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="occupation" className="text-xs font-semibold text-slate-700">Mesleğiniz</Label>
                    <Input id="occupation" placeholder="Örn: Yazılımcı" value={formData.occupation} onChange={handleFormInputChange} className="border-slate-200 focus-visible:ring-[#A8322D]/20 focus-visible:border-[#A8322D]" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="company" className="text-xs font-semibold text-slate-700">Çalıştığınız Kurum</Label>
                    <Input id="company" placeholder="Örn: Teknoloji Şirketi" value={formData.company} onChange={handleFormInputChange} className="border-slate-200 focus-visible:ring-[#A8322D]/20 focus-visible:border-[#A8322D]" />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="graduationYear" className="text-xs font-semibold text-slate-700">Mezuniyet Yılınız</Label>
                    <Input id="graduationYear" type="number" placeholder="Örn: 2024" value={formData.graduationYear} onChange={handleFormInputChange} className="border-slate-200 focus-visible:ring-[#A8322D]/20 focus-visible:border-[#A8322D]" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="linkedinLink" className="text-xs font-semibold text-slate-700">LinkedIn Profil Linkiniz</Label>
                    <Input id="linkedinLink" type="url" placeholder="https://linkedin.com/in/..." value={formData.linkedinLink} onChange={handleFormInputChange} className="border-slate-200 focus-visible:ring-[#A8322D]/20 focus-visible:border-[#A8322D]" />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="instagramLink" className="text-xs font-semibold text-slate-700">Instagram Profil Linkiniz</Label>
                    <Input id="instagramLink" type="url" placeholder="https://instagram.com/..." value={formData.instagramLink} onChange={handleFormInputChange} className="border-slate-200 focus-visible:ring-[#A8322D]/20 focus-visible:border-[#A8322D]" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="biography" className="text-xs font-semibold text-slate-700">Hakkımda</Label>
                  <textarea 
                    id="biography" 
                    placeholder="Kendinizden, çalışma alanlarınızdan veya öğrencilik yıllarınızdan kısaca bahsedin..." 
                    value={formData.biography}
                    onChange={handleFormInputChange}
                    className="flex min-h-[90px] w-full rounded-md border border-slate-200 bg-background px-3 py-2 text-sm transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A8322D]/20 focus-visible:border-[#A8322D]"
                  />
                </div>
                <div className="flex gap-4 pt-2">
                  <Button type="submit" className="flex-1 bg-[#A8322D] hover:bg-[#8b2723] text-white font-semibold transition-colors duration-200" size="lg">
                    Profilimi Kaydet
                  </Button>
                  <Button type="button" variant="outline" size="lg" className="flex-1 border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors duration-200" onClick={() => setIsFirstTime(false)}>
                    Sonra Güncelle
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      ) : (
        /* NORMAL KULLANICI GÖRÜNÜMÜ */
        <>
          {/* ÜST KISIM: Kapak ve Profil Resmi */}
          <Card className="overflow-hidden border-slate-200 shadow-sm rounded-2xl">
            <div className="relative h-48 md:h-64 bg-slate-100 group">
            
                <ImageWithFallback
                  src={userProfile.coverImage || DEFAULT_COVER_IMAGE}
                  fallbackSrc={DEFAULT_COVER_IMAGE}
                  alt="Cover"
                  className="w-full h-full object-cover object-bottom"
                />
            
              {isOwnProfile && (
                <div 
                  className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 cursor-pointer backdrop-blur-[2px]"
                  onClick={() => coverImageInputRef.current?.click()}
                >
                  <div className="flex items-center gap-2 bg-black/60 text-white px-4 py-2 rounded-full shadow-sm text-xs font-semibold">
                    <Camera className="w-4 h-4" />
                    <span>Kapak Fotoğrafını Değiştir</span>
                  </div>
                </div>
              )}
              <input 
                type="file" 
                ref={coverImageInputRef} 
                className="hidden" 
                accept="image/*" 
                onChange={(e) => handleImageUpload(e, 'cover')} 
              />
              <div className="absolute top-4 right-4 z-10">
                {isOwnProfile ? (
                  <Button onClick={() => navigate('/profil-ayarlar')} size="sm" className="gap-2 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 shadow-sm transition-all duration-200 text-xs font-semibold">
                    <Settings className="h-3.5 w-3.5 text-slate-500" />
                    Ayarlar
                  </Button>
                ) : (
                  <Button onClick={() => toast.info(`${userProfile.firstName} ile mesajlaşma yakında aktif olacak!`)} size="sm" className="gap-2 bg-[#A8322D] text-white hover:bg-[#8b2723] shadow-sm transition-all duration-200 text-xs font-semibold">
                    <MessageSquare className="h-3.5 w-3.5" />
                    Mesaj Gönder
                  </Button>
                )}
              </div>
            </div>

            <div className="relative px-6 pb-6">
              <div className="flex justify-center -mt-16 md:-mt-20 mb-4">
                {/* MODERN GLASSMORPHISM PROFİL ÇERÇEVESİ */}
                <div className="relative group rounded-full p-1 bg-white/40 backdrop-blur-md shadow-md border border-white/20 transition-all duration-300 hover:shadow-[0_0_25px_rgba(168,50,45,0.25)]">

                  <ImageWithFallback
                    src={userProfile.profileImage || DEFAULT_PROFILE_IMAGE}
                    fallbackSrc={DEFAULT_PROFILE_IMAGE}
                    alt="Profile"
                    className="w-32 h-32 md:w-40 md:h-40 rounded-full object-cover bg-white"
                  />

                  {isOwnProfile && (
                    <div 
                      className="absolute inset-1 rounded-full bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 cursor-pointer backdrop-blur-[1px]"
                      onClick={() => profileImageInputRef.current?.click()}
                    >
                      <Camera className="w-6 h-6 text-white" />
                    </div>
                  )}
                  <input 
                    type="file" 
                    ref={profileImageInputRef} 
                    className="hidden" 
                    accept="image/*" 
                    onChange={(e) => handleImageUpload(e, 'profile')} 
                  />
                  <Badge className="absolute bottom-2 right-2 bg-[#A8322D] hover:bg-[#8b2723] capitalize shadow-sm text-[11px] font-semibold border-none px-2.5 py-0.5 z-10">
                    {userProfile.userType === 'alumni' ? 'Mezun Üye' : userProfile.userType === 'teacher' ? 'Öğretmen' : 'Öğrenci'}
                  </Badge>
                </div>
              </div>

              <div className="text-center space-y-2">
                <div>
                  <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
                    {userProfile.firstName} {userProfile.lastName}
                  </h1>
                  <p className="text-sm font-medium text-slate-500">
                    {userProfile.occupation && userProfile.company 
                      ? `${userProfile.occupation} @ ${userProfile.company}`
                      : userProfile.occupation || userProfile.company || ""}
                  </p>
                </div>
                
                <div className="flex justify-center gap-2.5 pt-1">
                  {userProfile.linkedin && (
                    <a href={userProfile.linkedin} target="_blank" rel="noopener noreferrer" className="p-2 bg-slate-50 border border-slate-200 text-slate-600 rounded-full hover:text-[#0a66c2] hover:border-[#0a66c2]/30 hover:bg-[#0a66c2]/5 transition-all duration-200 shadow-sm">
                      <Linkedin className="h-4 w-4" />
                    </a>
                  )}
                  {userProfile.instagram && (
                    <a href={userProfile.instagram} target="_blank" rel="noopener noreferrer" className="p-2 bg-slate-50 border border-slate-200 text-slate-600 rounded-full hover:text-[#e1306c] hover:border-[#e1306c]/30 hover:bg-[#e1306c]/5 transition-all duration-200 shadow-sm">
                      <Instagram className="h-4 w-4" />
                    </a>
                  )}
                </div>

                <p className="text-sm text-slate-600 max-w-2xl mx-auto leading-relaxed pt-2">
                  {userProfile.bio || "Henüz bir biyografi eklenmemiş."}
                </p>
              </div>
            </div>
          </Card>

          {/* ALT KISIM: Bilgiler ve Sadece Bu Kullanıcıya Ait Duyurular */}
          <div className="grid lg:grid-cols-3 gap-6 items-start">
            
            {/* SOL TARAF */}
            <div className="lg:col-span-1 space-y-6">
              <Card className="border-slate-200 shadow-sm rounded-2xl hover:border-slate-300 transition-colors duration-200">
                <CardContent className="pt-5 space-y-4">
                  <h2 className="text-sm font-bold text-slate-900 border-b border-slate-100 pb-2 uppercase tracking-wider">Kişisel Bilgiler</h2>
                  <div className="space-y-3.5">
                    <div className="flex items-center gap-3 text-slate-700">
                      <div className="p-1.5 bg-slate-50 rounded-lg border border-slate-100 text-slate-500"><Mail className="h-4 w-4" /></div>
                      <div>
                        <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider leading-none mb-0.5">
                          E-posta
                        </div>

                        <a
                          href={`mailto:${userProfile.email}`}
                          className="text-sm text-blue-600 hover:underline"
                        >
                          {userProfile.email}
                        </a>
                      </div>
                    </div>
                    {userProfile.phone && (
                      <div className="flex items-center gap-3 text-slate-700">
                        <div className="p-1.5 bg-slate-50 rounded-lg border border-slate-100 text-slate-500"><Phone className="h-4 w-4" /></div>
                        <div>
                          <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider leading-none mb-0.5">Telefon</div>
                          <div className="text-sm font-medium text-slate-800">{userProfile.phone}</div>
                        </div>
                      </div>
                    )}
                    {userProfile.city && (
                      <div className="flex items-center gap-3 text-slate-700">
                        <div className="p-1.5 bg-slate-50 rounded-lg border border-slate-100 text-slate-500"><MapPin className="h-4 w-4" /></div>
                        <div>
                          <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider leading-none mb-0.5">Yaşadığı Şehir</div>
                          <div className="text-sm font-medium text-slate-800">{userProfile.city}</div>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="border-slate-200 shadow-sm rounded-2xl hover:border-slate-300 transition-colors duration-200">
                <CardContent className="pt-5 space-y-4">
                  <h2 className="text-sm font-bold text-slate-900 border-b border-slate-100 pb-2 uppercase tracking-wider">Eğitim & Kariyer</h2>
                  <div className="space-y-3.5">
                    {userProfile.graduationYear && (
                      <div className="flex items-center gap-3 text-slate-700">
                        <div className="p-1.5 bg-slate-50 rounded-lg border border-slate-100 text-slate-500"><GraduationCap className="h-4 w-4" /></div>
                        <div>
                          <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider leading-none mb-0.5">Mezuniyet Yılı</div>
                          <div className="text-sm font-medium text-slate-800">{userProfile.graduationYear}</div>
                        </div>
                      </div>
                    )}
                    {userProfile.department && (
                      <div className="flex items-center gap-3 text-slate-700">
                        <div className="p-1.5 bg-slate-50 rounded-lg border border-slate-100 text-slate-500"><GraduationCap className="h-4 w-4" /></div> 
                        <div>
                          <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider leading-none mb-0.5">Bölüm</div>
                          <div className="text-sm font-medium text-slate-800">{userProfile.department}</div>
                        </div>
                      </div>
                    )}
                    {userProfile.company && (
                      <div className="flex items-center gap-3 text-slate-700">
                        <div className="p-1.5 bg-slate-50 rounded-lg border border-slate-100 text-slate-500"><Briefcase className="h-4 w-4" /></div>
                        <div>
                          <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider leading-none mb-0.5">Çalıştığı Kurum</div>
                          <div className="text-sm font-medium text-slate-800">{userProfile.company}</div>
                        </div>
                      </div>
                    )}
                    {userProfile.occupation && (
                      <div className="flex items-center gap-3 text-slate-700">
                        <div className="p-1.5 bg-slate-50 rounded-lg border border-slate-100 text-slate-500"><Briefcase className="h-4 w-4" /></div>
                        <div>
                          <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider leading-none mb-0.5">Meslek / Ünvan</div>
                          <div className="text-sm font-medium text-slate-800">{userProfile.occupation}</div>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* SAĞ TARAF */}
            <div className="lg:col-span-2 space-y-6">
              
              {/* DUYURU OLUŞTURMA & DÜZENLEME KUTUSU */}
              {isOwnProfile && (
                <Card className={`border shadow-sm bg-white rounded-2xl transition-all duration-300 ${editingAnnouncementId ? 'border-[#A8322D] bg-[#A8322D]/5 shadow-md' : 'border-slate-200 hover:border-slate-300'}`}>
                  <CardContent className="pt-5 pb-4 px-5">
                    <div className="flex gap-3">
                      <ImageWithFallback
                        src={userProfile.profileImage || DEFAULT_PROFILE_IMAGE}
                        fallbackSrc={DEFAULT_PROFILE_IMAGE}
                        alt="Profile"
                        className="w-10 h-10 rounded-full object-cover shrink-0 border border-slate-100 shadow-sm"
                      />
                      <div className="w-full space-y-3">
                        
                        {/* Başlık Değişimi */}
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold text-[#A8322D] uppercase tracking-wider">
                            {editingAnnouncementId ? 'Duyuruyu Düzenle' : 'Yeni Duyuru Oluştur'}
                          </span>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3">
                          <select
                            value={newAnnouncement.category}
                            onChange={(e) => setNewAnnouncement({...newAnnouncement, category: e.target.value})}
                            className="h-9 rounded-md border border-slate-200 bg-slate-50 px-3 text-xs font-semibold focus:outline-none focus:border-[#A8322D] text-slate-700 sm:w-44 cursor-pointer"
                          >
                            <option value="GENEL">Genel Duyuru</option>
                            <option value="IS_ILANI">İş İlanı / Kariyer</option>
                            <option value="HABER">Haber</option>
                            <option value="VEFAT">Vefat & Başsağlığı</option>
                          </select>
                          
                          <Input
                            placeholder="Duyuru Başlığı"
                            value={newAnnouncement.title}
                            onChange={(e) => setNewAnnouncement({...newAnnouncement, title: e.target.value})}
                            className="flex-1 bg-slate-50 border-slate-200 h-9 text-sm focus-visible:ring-0 focus-visible:border-[#A8322D]"
                          />
                        </div>

                        <textarea
                          placeholder="Meslektaşlarınıza bir duyuru paylaşın..."
                          value={newAnnouncement.content}
                          onChange={(e) => setNewAnnouncement({...newAnnouncement, content: e.target.value})}
                          className="w-full min-h-[60px] p-2 bg-transparent focus:outline-none resize-none text-slate-800 placeholder:text-slate-400 text-sm leading-relaxed"
                        />
                        
                        <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                          <div className="flex items-center gap-2 mt-1">
                            <Label 
                              htmlFor="post-file" 
                              className="flex items-center justify-center w-8 h-8 text-slate-500 bg-slate-50 hover:bg-slate-100 rounded-full cursor-pointer transition-colors border border-slate-200 shadow-sm"
                              title="Görsel Ekle"
                            >
                              <Camera className="w-4 h-4" />
                            </Label>
                            <input 
                              id="post-file" 
                              type="file" 
                              accept="image/*"
                              className="hidden" 
                              onChange={handleFileChange}
                            />
                            {selectedFile && (
                              <div className="flex items-center bg-slate-50 px-3 py-1 rounded-full border border-slate-200 shadow-sm">
                                <span className="text-slate-600 text-xs font-medium truncate max-w-[120px] mr-2">
                                  {selectedFile.name}
                                </span>
                                <button onClick={() => setSelectedFile(null)} className="text-slate-400 hover:text-red-600 text-xs font-bold">
                                  ✕
                                </button>
                              </div>
                            )}
                          </div>

                          <div className="flex items-center gap-2 mt-1">
                            {editingAnnouncementId && (
                              <Button 
                                onClick={() => {
                                  setEditingAnnouncementId(null);
                                  setNewAnnouncement({ title: '', description: '', content: '', category: 'GENEL' });
                                  setSelectedFile(null);
                                }}
                                variant="outline"
                                className="border-slate-200 hover:bg-slate-100 text-slate-700 font-semibold rounded-md px-4 h-8 text-xs transition-colors"
                              >
                                İptal
                              </Button>
                            )}
                            <Button 
                              onClick={handleSaveAnnouncement} 
                              disabled={!newAnnouncement.title.trim() || !newAnnouncement.content.trim() || isPosting}
                              className="bg-[#A8322D] hover:bg-[#8b2723] text-white font-semibold rounded-md px-5 h-8 text-xs transition-colors duration-200 shadow-sm"
                            >
                              {isPosting ? 'İşleniyor...' : editingAnnouncementId ? 'Değişiklikleri Kaydet' : 'Duyuruyu Paylaş'}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* DUYURU AKIŞI ALANI */}
              <div className="space-y-0 bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
                <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2 bg-slate-50/50">
                  <Megaphone className="w-4 h-4 text-[#A8322D]" />
                  <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">
                    {isOwnProfile ? 'Duyurular ve İlanlar' : `${userProfile.firstName} Kullanıcısının Duyuruları`}
                  </h3>
                </div>
                
                {announcements.length === 0 ? (
                  <div className="text-center py-16 px-4">
                    <p className="text-slate-400 text-sm">Yayınlanmış bir duyuru bulunmuyor.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {announcements.map((announcement) => (
                      <div 
                        key={announcement.id} 
                        className="p-4 sm:p-5 bg-white hover:bg-slate-50/40 hover:shadow-[0_4px_12px_rgba(0,0,0,0.03)] transition-all duration-300 relative group/card hover:-translate-y-0.5 border-x border-transparent hover:border-slate-200/60 first:rounded-t-none last:rounded-b-none"
                      >
                        <div className="flex justify-between items-start mb-2 gap-4">
                          {/* TIKLANABİLİR BAŞLIK VE İÇERİK GRUBU */}
                          <div 
                            className="flex-1 cursor-pointer group/link"
                            onClick={() => navigate(`/duyurular/${announcement.id}`)}
                          >
                            <div className="flex items-center gap-1.5 mb-1.5">
                              <span className="font-bold text-slate-800 text-xs transition-colors">
                                {userProfile.firstName} {userProfile.lastName}
                              </span>
                              <span className="text-slate-400 text-[11px]">
                                · {new Date(announcement.PublishDate).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' })}
                              </span>
                            </div>
                            
                            <h4 className="text-sm font-bold text-slate-900 leading-snug group-hover/link:text-[#A8322D] transition-colors mb-1.5">
                              {announcement.Title}
                            </h4>
                            
                            <p className="text-slate-600 leading-relaxed text-xs line-clamp-2">
                              {announcement.Content}
                            </p>
                          </div>
                          
                          <div className="flex items-center gap-1.5 self-start shrink-0">
                            <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-600 border-slate-200 font-semibold px-2 py-0.5 rounded-md shadow-sm">
                              {announcement.Category === 'GENEL' ? 'Genel' : 
                              announcement.Category === 'IS_ILANI' ? 'İş İlanı' : 
                              announcement.Category === 'HABER' ? 'Haber' : 'Vefat'}
                            </Badge>

                            {/* DÜZENLEME VE SİLME BUTONLARI */}
                            {(isOwnProfile || isAdmin) && (
                              <div className="flex items-center gap-1">
                                {/* Düzenle Butonu */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation(); 
                                    handleEditAnnouncementClick(announcement);
                                  }}
                                  className="text-slate-400 hover:text-blue-600 p-1 rounded-md hover:bg-blue-50 transition-all duration-200 border border-transparent"
                                  title="Duyuruyu Düzenle"
                                >
                                  <Edit2 className="w-3.5 h-3.5" />
                                </button>

                                {/* Sil Butonu */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation(); 
                                    handleDeleteAnnouncement(announcement.id);
                                  }}
                                  className="text-slate-400 hover:text-[#A8322D] p-1 rounded-md hover:bg-[#A8322D]/5 transition-all duration-200 border border-transparent"
                                  title="Duyuruyu Sil"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* GÖRSEL ALANI */}
                        {announcement.Image && (
                          <div 
                            className="mt-3 rounded-xl overflow-hidden border border-slate-100 bg-slate-50 max-h-48 max-w-md flex items-center justify-center cursor-pointer hover:border-slate-200 transition-all duration-200 shadow-sm"
                            onClick={() => navigate(`/duyurular/${announcement.id}`)}
                          >
                            <ImageWithFallback
                              src={getFullImageUrl(announcement.Image)}
                              fallbackSrc={DEFAULT_COVER_IMAGE}
                              alt="Duyuru Görseli"
                              className="w-full h-auto object-cover max-h-48"
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}